import { memo, Fragment, useState, useEffect, useCallback, createRef, ReactElement } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { cloneDeep, isEmpty } from 'lodash'
import { detect } from 'detect-browser'
import { PopupWindow } from 'beam-v11'
import IconButton from 'beam/dist/IconButton'
import BeamIcon from 'beam/dist/BeamIcon'
import Panel from 'beam/dist/Panel'
import { CircularProgress } from 'beam/dist/Progress'
import { makeStyles } from 'beam/dist/styles'
import { ConditionalWrap } from 'beam/dist/utils'
import { EVAL_STATE, RESPONSE_STATE } from './evaluationForm.constants'
import EvaluationFormMultiMenu from './evaluationFormMultiMenu'
import { useAuthenticatedUser } from '../../../Permissions'
import EvaluationForm from './EvaluationForm'
import { difference } from '../../../Common/Utils'
import { useReactToPrint } from 'react-to-print'
import { DraftComment, Eval } from './evaluationForm.api'

import { Tabs } from 'beam/dist/Tabs'
import Tab from '@mui/material/Tab'
import { IndexedChatBubble } from '../ChatMapPanel/constants'
import { useAppSelector } from '../../../root.reducer'

export const dataTestIdPrefix = 'App-Recordings-MediaPlayer-Evaluation-'

const POPUP_WINDOW_NAME = 'evaluation-window'

const useStyles = makeStyles(theme => ({
  root: {},
  header: {
    '@media print': {
      display: 'none',
    },
  },
  panelRoot: {
    maxWidth: '100%',
  },
  undockedPanel: {
    width: '100vw',
  },
  panelBody: {
    height: `calc(100% - ${theme.spacing(4)}px)`,
  },
  panelHeaderActions: {
    alignItems: 'center',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: '1 1 auto',
  },
  tabRoot: {
    color: '#64727C',
    '& .Mui-selected': {
      color: '#006FE7',
      padding: '12px 0px',
      margin: '0px 16px',
    },
    '& .MuiButtonBase-root': {
      opacity: 1,
    },
    '& .MuiButtonBase-root span.MuiTouchRipple-root': {
      display: 'none',
    },
  },
  evaFormAuto: {
    paddingTop: '0px !important',
  },
  evaFormManual: {
    paddingTop: '15px !important',
  },
  tabIndicator: {
    backgroundColor: '#006FE7',
    height: '2px',
  },
  flexContainer: {
    background: '#fafafa',
  },
  tabTitle: {
    fontSize: '15px',
  },
  tabSubTitle: {
    fontSize: '12px',
    fontWeight: 400,
  },
  showIframe: {
    display: 'block',
  },
  hideIframe: {
    display: 'none',
  },
}))

interface EvaluationPanelProps {
  activeForms: $TSFIXME[],
  contactId: number,
  selectedForm: $TSFIXME,
  sectionsBeingUpdated: $TSFIXME[],
  callDuration: number,
  evalId?: number | null,
  qualityEvaluationAllowed: boolean,
  calibrationEvaluationAllowed: boolean,
  template?: $TSFIXME,
  values?: Eval | null,
  comments: $TSFIXME,
  defaultAnswers?: $TSFIXME,
  hasValidAnswers: boolean,
  isValuesLoading: boolean,
  isSavingValues: boolean,
  evaluationLoaded: boolean,
  isCalibrationView: boolean,
  onSetEvaluationView: (...args: $TSFIXME[]) => $TSFIXME,
  onSetCalibrationView: (...args: $TSFIXME[]) => $TSFIXME,
  onGetEvalForm: (...args: $TSFIXME[]) => $TSFIXME,
  onGetEvalComments: (...args: $TSFIXME[]) => $TSFIXME,
  onSaveEval: (...args: $TSFIXME[]) => $TSFIXME,
  onResetEval: (...args: $TSFIXME[]) => $TSFIXME,
  onAddComment: (contactId: number, evalId: number | null, comment: DraftComment) => void,
  onUpdateComment: (comment: DraftComment, ref: string) => void,
  onGetMediaCurrentTime: (...args: $TSFIXME[]) => $TSFIXME,
  isDocked: boolean,
  setDocked: (...args: $TSFIXME[]) => $TSFIXME,
  arePopupsEnabled?: boolean,
  onPopupError: (...args: $TSFIXME[]) => $TSFIXME,
  onPopupsBlocked: (...args: $TSFIXME[]) => $TSFIXME,
  agentDisplayName: string,
  contactDateTime: number,
  showManualEvalForm: boolean,
  showAiEvalForm: boolean,
  indexedChatBubbles: IndexedChatBubble[],
  autoQAHasData: boolean,
  autoQAFormId: number,
}

const STORAGE_KEY = 'EvaluationPanel_Docked'
const EVALUATION_TAB_AUTO = 'autoEvaluation'
const EVALUATION_TAB_MANUAL = 'manualEvaluation'

const EvaluationPanel = ({
  contactId,
  evalId = null,
  values = null,
  template = null,
  defaultAnswers = {},
  isCalibrationView,
  arePopupsEnabled = true,
  onPopupsBlocked,
  onPopupError,
  onGetEvalComments,
  onAddComment,
  onUpdateComment,
  onSaveEval,
  onResetEval,
  onGetEvalForm,
  setDocked,
  onGetMediaCurrentTime,
  activeForms,
  selectedForm,
  sectionsBeingUpdated,
  evaluationLoaded,
  isValuesLoading,
  isSavingValues,
  callDuration,
  isDocked,
  hasValidAnswers,
  qualityEvaluationAllowed,
  calibrationEvaluationAllowed,
  onSetEvaluationView,
  onSetCalibrationView,
  comments = null,
  agentDisplayName,
  contactDateTime,
  showManualEvalForm,
  showAiEvalForm,
  indexedChatBubbles,
  autoQAHasData,
  autoQAFormId,
}: EvaluationPanelProps): ReactElement => {
  const [showAppealForm, setShowAppealForm] = useState(false)
  // This is needed to get around a bug in React portals where the popup window won't trigger events
  // until it gets a state change (even if the state change isn't used, which is the case here)
  const [_isWindowReady, setIsWindowReady] = useState(false)
  const [isUnmounting, setIsUnmounting] = useState(false)

  const browser = detect()
  const classes = useStyles()
  const { t } = useTranslation()
  const authenticatedUser = useAuthenticatedUser()
  const evalFormRef = createRef<HTMLDivElement>()
  const autoEvalIframeRef = createRef<HTMLIFrameElement>()

  const printEvaluationDocked = useReactToPrint({
    content: () => evalFormRef.current,
  })

  const evaluationIFrameSetMessageTime = (time: number) => {
    // send message to the Auto QM iframe for printing
    autoEvalIframeRef?.current?.contentWindow?.postMessage({ channel: 'C1MediaPlayer', action: 'AutoQA.AutoEval.setMessageTime', time, callDuration }, '*')
  }

  const { user } = authenticatedUser
  // @ts-expect-error TODO: switch `useAuthenticatedUser` to `useEnsuredUser` hook
  const userTimezone = user.timezoneString

  const stateDesc = {
    [EVAL_STATE.UNSCORED]: t('unscored'),
    [EVAL_STATE.SCORED]: t('scored'),
    [EVAL_STATE.IN_PROGRESS]: t('inProgress'),
    [EVAL_STATE.NEEDS_APPROVAL]: t('needsApproval'),
    [EVAL_STATE.CANNOT_SCORE]: t('cannotScore'),
    [EVAL_STATE.NEEDS_CANNOT_SCORE_APPROVAL]: t('cannotScoreApproval'),
  }

  useEffect(() => {
    const lastState = window.localStorage.getItem(STORAGE_KEY)
    if (lastState) {
      setDocked(JSON.parse(lastState))
    }

    return () => {
      setIsUnmounting(true)
    }
  }, [])

  useEffect(() => {
    setTabValue((showAiEvalForm && autoQAHasData) ? EVALUATION_TAB_AUTO : EVALUATION_TAB_MANUAL)
  }, [showAiEvalForm, autoQAHasData])

  useEffect(() => {
    evaluationIFrameSetMessageTime(onGetMediaCurrentTime())
  }, [onGetMediaCurrentTime()])

  const handleDocking = () => {
    setDocked(true)

    setIsWindowReady(false)

    if (!isUnmounting) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(true))
    }
  }

  const handlePopOut = () => {
    if (!arePopupsEnabled) {
      onPopupsBlocked()
    } else {
      setDocked(false)
      setIsWindowReady(false)

      if (!isUnmounting) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(false))
      }
    }
  }

  const _handleWindowReady = () => {
    setIsWindowReady(true)
  }

  const _handlePopupError = () => {
    onPopupError()
    handleDocking()
  }

  const handleCommentAdd = async (comment: DraftComment) => {
    onAddComment(contactId, evalId, comment)
  }

  const handleAnswerClick = useCallback(answer => {
    const newValues = cloneDeep(values)
    // @ts-expect-error TODO: handle null values
    newValues.sections
      .find(({ id }) => id === answer.sectionId).questions
      .find(({ id }) => id === answer.questionId).selectedOption = answer.selectedOption
    // @ts-expect-error TODO: handle null values
    newValues.evaluatedTz = newValues.evaluatedTz || userTimezone
    // @ts-expect-error TODO: handle null values
    newValues.state.stateId = EVAL_STATE.IN_PROGRESS
    // @ts-expect-error TODO: handle null values
    onSaveEval(contactId, values.id, newValues)
  }, [values, contactId, isCalibrationView])

  const handleActiveFormChange = (selectedForm: $TSFIXME) => {
    if (selectedForm !== null) {
      onGetEvalForm(selectedForm.id)
    }
  }

  const saveEvaluationState = (newState: $TSFIXME) => {
    const newValues = cloneDeep(values)
    // @ts-expect-error TODO: handle null values
    newValues.state.stateId = newState
    // @ts-expect-error TODO: handle null values
    onSaveEval(contactId, values.id, newValues)
  }

  const cannotScoreEvaluation = () => {
    saveEvaluationState(
      template.approvalRequired && !isCalibrationView ? EVAL_STATE.NEEDS_CANNOT_SCORE_APPROVAL : EVAL_STATE.CANNOT_SCORE,
    )
  }

  const completeEvaluation = () => {
    saveEvaluationState(
      template.approvalRequired && !isCalibrationView ? EVAL_STATE.NEEDS_APPROVAL : EVAL_STATE.SCORED,
    )
  }

  const editEvaluation = () => {
    saveEvaluationState(EVAL_STATE.IN_PROGRESS)
  }

  const approveEvaluation = () => {
    saveEvaluationState(EVAL_STATE.SCORED)
  }

  const rejectEvaluation = () => {
    saveEvaluationState(EVAL_STATE.IN_PROGRESS)
  }

  const acknowledgeEvaluation = () => {
    const newValues = {
      ...cloneDeep(values),
      responseState: {
        stateId: RESPONSE_STATE.ACKNOWLEDGED,
      },
      responseDate: Date.now(),
    }

    onSaveEval(contactId, evalId, newValues)
  }

  const appealEvaluation = useCallback(() => {
    setShowAppealForm(true)
  }, [])

  const handleAppealCancel = useCallback(() => {
    setShowAppealForm(false)
  }, [])

  const handleAppealSubmit = async (reasonText: $TSFIXME) => {
    const newValues = {
      ...cloneDeep(values),
      responseState: {
        stateId: RESPONSE_STATE.APPEALED,
        reason: reasonText,
      },
      responseDate: Date.now(),
    }

    setShowAppealForm(false)

    await onSaveEval(contactId, evalId, newValues)
    onGetEvalComments(contactId, evalId)
  }

  const resetEvaluation = () => {
    onResetEval(contactId, evalId)
  }

  const printEvaluationPopup = () => {
    // get a reference to the popup window
    const evalWindow = window.open('', POPUP_WINDOW_NAME)
    // @ts-expect-error TODO: handle null evalWindow
    evalWindow.print()
  }

  const setUnansweredQuestionsToDefault = () => {
    const newValues = {
      ...values,
      // @ts-expect-error TODO: handle null values
      sections: values.sections.map(section => ({
        ...section,
        questions: section.questions.map(question => ({
          ...question,
          selectedOption:
            question.selectedOption || defaultAnswers[question.id],
        })),
      })),
    }

    newValues.evaluatedTz = newValues.evaluatedTz || userTimezone
    // @ts-expect-error TODO: handle undefined newValues.state
    newValues.state.stateId = EVAL_STATE.IN_PROGRESS

    onSaveEval(contactId, evalId, newValues)
  }

  const _handlePopupClose = (_: unknown = null, wasClosedByParent = false) => {
    if (!wasClosedByParent) {
      handleDocking()
    }
  }
  // @ts-expect-error TODO: handle undefined values.state
  const evalState = `${!isValuesLoading && values ? ' (' + stateDesc[values.state.stateId] + ')' : ''}`
  const panelTitle = `${isCalibrationView ? t('calibration') : t('evaluation')}`
  const evaluationScored = values !== null && values.state.stateId === EVAL_STATE.SCORED

  const webUrl = useAppSelector(state => state.app.appProperties?.convIntelligenceWebUrl)
  const authHost = window.location.host

  const sessionId = user?.sessionId
  const iframeUrl = webUrl ? `${webUrl}/#/autoQA/aiEvaluation/?host=${authHost}&sid=${sessionId}&contactId=${contactId}&formId=${autoQAFormId}` : null
  const [tabValue, setTabValue] = useState(showAiEvalForm && autoQAHasData ? EVALUATION_TAB_AUTO : EVALUATION_TAB_MANUAL)

  const headerActions = (
    <Fragment>
      {/* @ts-expect-error TODO: handle null browser */}
      {browser.name === 'ie' ? null : (
        // @ts-expect-error TODO: migrate to MUI v5
        <IconButton
          aria-label="popoutEvalButton"
          color="inherit"
          dataTestId={`${dataTestIdPrefix}popoutEvalButton`}
          onClick={isDocked ? handlePopOut : handleDocking}
        >
          <BeamIcon>{isDocked ? 'OpenInNew' : 'OpenInApp'}</BeamIcon>
        </IconButton>
      )}
      {(tabValue === EVALUATION_TAB_MANUAL) && (
        <EvaluationFormMultiMenu
          values={values}
          template={template}
          allAnswersValid={hasValidAnswers}
          qualityEvaluationAllowed={qualityEvaluationAllowed}
          calibrationEvaluationAllowed={calibrationEvaluationAllowed}
          calibrationView={isCalibrationView}
          onSetEvaluationView={onSetEvaluationView}
          onSetCalibrationView={onSetCalibrationView}
          onCannotScoreEvaluation={cannotScoreEvaluation}
          onCompleteEvaluation={completeEvaluation}
          onResetEvaluation={resetEvaluation}
          onSetUnansweredQuestionsToDefault={setUnansweredQuestionsToDefault}
          onEditEvaluation={editEvaluation}
          onApproveEvaluation={approveEvaluation}
          onRejectEvaluation={rejectEvaluation}
          onAcknowledgeEvaluation={acknowledgeEvaluation}
          onAppealEvaluation={appealEvaluation}
          onPrintEvaluation={isDocked ? printEvaluationDocked : printEvaluationPopup}
        />
      )}
    </Fragment>
  )

  return (
    <ConditionalWrap
      condition={arePopupsEnabled && !isDocked}
      wrap={children => (
        <PopupWindow
          id={POPUP_WINDOW_NAME}
          title={t('evaluation')}
          onReady={_handleWindowReady}
          onError={_handlePopupError}
          onClose={_handlePopupClose}
          errorText={t('popupBlockerEnabled')}
        >
          {children}
        </PopupWindow>
      )}
    >
      {/* @ts-expect-error TODO: migrate to MUI v5 */}
      <Panel
        titleIcon={<BeamIcon>PlaylistCheck</BeamIcon>}
        title={panelTitle}
        dense
        variant="light"
        dataTestId={`${dataTestIdPrefix}panel`}
        headerActions={headerActions}
        headerTestId={`${dataTestIdPrefix}header`}
        classes={{
          root: classNames(classes.panelRoot, { [classes.undockedPanel]: arePopupsEnabled && !isDocked }),
          header: classes.header,
          body: classes.panelBody,
          actions: classNames(classes.panelHeaderActions),
        }}
      >
        <Tabs
          onChange={(e, value) => {
            setTabValue(value)
          }}
          value={tabValue}
          classes={{ root: classes.tabRoot, indicator: classes.tabIndicator, flexContainer: classes.flexContainer }}
        >
          {showAiEvalForm && autoQAHasData && (<Tab className={classes.evaFormAuto} {...{ 'data-testid': `${dataTestIdPrefix}autoTab` }} label={
            <Fragment>
              <span className={classes.tabTitle}>{t('evaFormAutoEvaluation')}</span>
            </Fragment>
          } value={EVALUATION_TAB_AUTO} style={{ textTransform: 'none' }} />)}
          {showManualEvalForm && (<Tab className={classes.evaFormManual} {...{ 'data-testid': `${dataTestIdPrefix}manualTab` }} label={
            <Fragment>
              <span className={classes.tabTitle}>{t('evaFormManualEvaluation')}
              </span>
              <span className={classes.tabSubTitle}>{evalState}&nbsp;</span>
            </Fragment>
          } value={EVALUATION_TAB_MANUAL} style={{ textTransform: 'none' }} />)}
        </Tabs>
        {(showAiEvalForm) && (
          <Fragment>
            {iframeUrl
              ? <iframe ref={autoEvalIframeRef} className={tabValue === EVALUATION_TAB_AUTO ? classes.showIframe : classes.hideIframe} title={t('autoQA')} src={ iframeUrl } height={ '100%' } width={ '100%' } style={ { 'border': 0, visibility: autoQAHasData ? 'visible' : 'hidden' } } />
              : <div></div>
            }
          </Fragment>
        )}

        {(tabValue === EVALUATION_TAB_MANUAL && evaluationLoaded) ? (
          <EvaluationForm
            values={values}
            template={template}
            comments={comments}
            activeForms={activeForms}
            selectedForm={selectedForm}
            sectionsBeingUpdated={sectionsBeingUpdated}
            callDuration={callDuration}
            userTimezone={userTimezone}
            isCalibrationView={isCalibrationView}
            isEvaluationScored={evaluationScored}
            isSavingValues={isSavingValues}
            showAppealForm={showAppealForm}
            onAppealSubmit={handleAppealSubmit}
            onAppealCancel={handleAppealCancel}
            onActiveFormChange={handleActiveFormChange}
            onGetCallTime={onGetMediaCurrentTime}
            onCommentAdd={handleCommentAdd}
            onCommentUpdate={onUpdateComment}
            onAnswerClick={handleAnswerClick}
            contactId={contactId}
            agentDisplayName={agentDisplayName}
            contactDateTime={contactDateTime}
            ref={evalFormRef}
            indexedChatBubbles={indexedChatBubbles}
          />
        ) : (tabValue === EVALUATION_TAB_MANUAL && !evaluationLoaded) && (
          <div className={classes.centered}>
            <CircularProgress color="secondary" />
          </div>
        )}
      </Panel>
    </ConditionalWrap>
  )
}

export default memo(EvaluationPanel, (prevProps, nextProps) => {
  const diff = difference(nextProps, prevProps)
  return isEmpty(diff)
})
